'use client';

import { useEffect, useState } from 'react';
import { Cookie, ChevronRight } from 'lucide-react';
import { useConsent } from '@/hooks/use-consent';
import { useAnalytics } from '@/hooks/use-analytics';
import { useAuth } from '@/components/auth-provider';
import { BottomSheet } from '@/components/bottom-sheet';
import {
  defaultPrefsFor,
  getConsentPreferences,
  type ConsentPreferences,
  type ConsentRegime,
} from '@/lib/consent';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { promoteStagedAttribution } from '@/components/referral-capture';
import { useT } from '@/components/i18n-provider';

const OPEN_EVENT = 'rb:open-cookie-preferences';

/**
 * Re-open the consent sheet from anywhere (Settings, footer), with the
 * user's current choice loaded. GDPR Art. 7(3) requires withdrawing
 * consent to be as easy as giving it, so there has to be a route back
 * after the first decision; without this the sheet could only ever open
 * once and a choice was permanent.
 *
 * A window event rather than a context: the banner is mounted once at
 * the app root and the callers are far away in the tree, so this avoids
 * threading a provider through for one button.
 */
export function openCookiePreferences(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/**
 * Cookie / tracking consent banner. Only shown to authenticated
 * users — anonymous visitors don't see it because we never set
 * non-essential (third-party) cookies for them in the first place:
 *
 *   - `AnalyticsProvider` only loads the GA4 + Google Ads adapters
 *     when `consent === 'accepted'`, and `useConsent()` returns
 *     null until the user explicitly accepts. So for anon visitors
 *     those adapters never run and no third-party cookies are set.
 *   - The first-party identity cookies (`rb_anon_id`,
 *     `rb_session_id`) are functional/essential under GDPR Art. 6
 *     legitimate-interest + the ePrivacy Directive's "strictly
 *     necessary" exemption — they don't require prior consent.
 *
 * Deferring the prompt until signup avoids the "blocks 5% of the
 * landing canvas" friction without changing what cookies get set,
 * and consent is collected at the first moment the user might opt
 * into the optional adapters (which only fire once they're authed
 * and actively using the product).
 *
 * If we ever start firing third-party trackers on anon traffic
 * the gate below must move back to "show banner whenever consent
 * is null" — keep this comment as a tripwire.
 *
 * KNOWN GAP, tracked as E13 in the compliance plan: the
 * strictly-necessary justification above is contested for
 * `rb_anon_id` specifically, because the privacy policy states its
 * purpose as measuring first-party usage and linking activity across
 * sign-in. Identity stitching is not strictly necessary to deliver a
 * service the user asked for, and the id feeds attribution and the
 * tracking pipeline rather than aggregate statistics. The fix is to
 * split it into a genuinely necessary session id plus a separate
 * consented measurement id. Until that lands, this comment asserts a
 * legal conclusion the published policy does not fully support — do
 * not cite it as settled.
 */
export function CookieConsentBanner() {
  const t = useT();
  const { consent, acceptAll, rejectAll, savePreferences } = useConsent();
  const { trackConsentChoice } = useAnalytics();
  const { user, loading } = useAuth();
  const [showSettings, setShowSettings] = useState(false);
  const [reopened, setReopened] = useState(false);
  // Which consent law applies, resolved SERVER-side from the ingress
  // `x-geo-country` by /api/consent-regime. It used to ride the runtime-config
  // injection, but that required a `headers()` read in the root layout, which
  // made every route in the app dynamic and uncacheable.
  //
  // The trust model is unchanged: the browser still never supplies its own
  // country, it only receives the server's verdict. What changed is delivery —
  // an injected value became a fetch.
  //
  // Starts at the stricter 'opt-in' and only ever moves after the server
  // answers, so a slow or failed request cannot downgrade an EEA/UK/CH visitor
  // into the opt-out flow. The banner is gated on `!!user` below, so in
  // practice the fetch resolves long before anything renders.
  const [regime, setRegime] = useState<ConsentRegime>(
    () => getRuntimeConfig().consentRegime ?? 'opt-in',
  );
  useEffect(() => {
    let cancelled = false;
    fetch('/api/consent-regime')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && (d?.regime === 'opt-in' || d?.regime === 'opt-out')) setRegime(d.regime);
      })
      .catch(() => {
        /* keep the stricter default */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Seeded from the stricter 'opt-in' default, because `regime` above starts
  // there and only moves once /api/consent-regime answers.
  const [prefs, setPrefs] = useState<ConsentPreferences>(() => defaultPrefsFor(regime));

  // Adopt the regime's defaults once the server's verdict lands.
  //
  // This used to be missing, and the lazy initializer above runs exactly once —
  // with the initial 'opt-in'. So a US visitor got the CPRA wording
  // ("Do Not Sell or Share…", "Turn this off to opt out") rendered over a
  // toggle that had opened OFF, which is the opt-in default: the copy
  // contradicted the control it sat on, and the opt-out flow never actually
  // ran. Observed on production 2026-08-09.
  //
  // Gated on there being NO choice on record: a visitor who has already decided
  // must never be silently flipped by a later regime resolution. That is also
  // why this cannot simply re-derive on every render.
  useEffect(() => {
    if (getConsentPreferences() !== null) return;
    setPrefs(defaultPrefsFor(regime));
  }, [regime]);

  // Re-opened from Settings or the footer: load the choice already on
  // record so the toggles show current state rather than defaults, and
  // go straight to the detail view, which is what the entry point promised.
  // Depends on `regime` so the defaults underlying a partial stored choice are
  // the resolved regime's, not the pre-fetch placeholder's.
  useEffect(() => {
    const onOpen = () => {
      setPrefs({ ...defaultPrefsFor(regime), ...(getConsentPreferences() ?? {}) });
      setShowSettings(true);
      setReopened(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [regime]);

  // The optional category IS the sale/share control — under CPRA, loading the
  // Google Ads adapter for cross-context behavioural advertising is the
  // "sharing" the right applies to. So the opt-out regime RELABELS this one
  // toggle rather than adding a second: two switches over a single stored
  // preference is how they desync, and an inverted "Do Not Sell = on" switch
  // over a `thirdParty = allowed` field is a bug waiting to happen. Toggle
  // semantics stay constant everywhere (on = allowed); only the wording and
  // the section heading change.
  const CATEGORIES: { key: keyof ConsentPreferences; label: string; description: string; locked?: boolean }[] = [
    {
      key: 'essential',
      label: t('cookies.essential'),
      description: t('cookies.essentialDesc'),
      locked: true,
    },
    {
      key: 'thirdParty',
      label: regime === 'opt-out' ? t('cookies.saleShare') : t('cookies.thirdParty'),
      description:
        regime === 'opt-out' ? t('cookies.saleShareDesc') : t('cookies.thirdPartyDesc'),
    },
  ];

  // Stay closed for anon visitors and during the auth-loading
  // flicker. `consent === null` alone used to gate this — now we
  // also require an authenticated user so the prompt lands after
  // signup (see the comment block on this component for the GDPR
  // rationale). The old `?onboarding=1` defer was dropped with the
  // sketch overlay; the empty-state page no longer frosts the canvas.
  const open = reopened || (consent === null && !loading && !!user);

  // A first-run prompt cannot be dismissed without choosing, so the user
  // makes an actual decision. A re-opened sheet closes freely: they have
  // already decided and are only reviewing.
  const handleClose = () => {
    if (reopened) {
      setReopened(false);
      setShowSettings(false);
    }
  };

  // Changing an EXISTING choice reloads the page. useConsent state is
  // per-component-instance, so AnalyticsProvider's copy does not see this
  // sheet's write, and an already-loaded GA adapter would keep firing until
  // the next full navigation. For withdrawal that is not cosmetic: Art. 7(3)
  // withdrawal has to actually stop the processing. Reload is the blunt but
  // honest way to guarantee it in both directions. First-run choices skip
  // the reload: nothing consent-gated has loaded yet.
  const applyChangedChoice = () => {
    if (reopened) window.location.reload();
    else handleClose();
  };

  const handleToggle = (key: keyof ConsentPreferences) => {
    if (key === 'essential') return;
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Referral / ad-click captures made before the choice are staged in
  // sessionStorage (components/referral-capture.tsx). Promote them to the real
  // cookies in the SAME click that grants consent: ReferralCapture also polls
  // for the decision, but a visitor who accepts and immediately hits "Sign up"
  // can leave the origin first, and the OAuth callback would then see no
  // referral cookie. Promotion re-checks consent itself, so a decline is a
  // no-op here (savePreferences already cleared staging via the consent gate).
  const handleAcceptAll = () => {
    trackConsentChoice('accept_all');
    acceptAll();
    promoteStagedAttribution();
    applyChangedChoice();
  };

  // No promoteStagedAttribution here: a refusal must not promote staged
  // referral captures into real cookies. The consent gate clears staging
  // on reject, and promotion re-checks consent anyway, but not calling it
  // keeps the refusal path obviously inert.
  const handleRejectAll = () => {
    trackConsentChoice('reject');
    rejectAll();
    applyChangedChoice();
  };

  const handleSave = () => {
    trackConsentChoice('save_preferences');
    savePreferences(prefs);
    promoteStagedAttribution();
    applyChangedChoice();
  };

  return (
    <BottomSheet
      open={open}
      onClose={handleClose}
      elevated={reopened}
      // First run rides the shared toast column, alongside the other prompts.
      // A deliberate re-open is summoned FROM Settings, so it has to clear the
      // dialog it was launched from: the toast column sits below z-[200] and
      // the panel would render visible but unclickable behind the modal.
      {...(reopened
        ? {
            desktopClassName:
              'fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] w-[min(28rem,calc(100vw-2rem))]',
          }
        : { desktopStackSlot: 'default' as const })}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Cookie className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{t('cookies.preferences')}</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {t('cookies.description')}{' '}
            <a href="/doc/privacy-policy" className="underline hover:text-foreground transition-colors">
              {t('cookies.privacyPolicy')}
            </a>
          </p>

          {!showSettings ? (
            <div className="flex items-center gap-2 mt-3">
              {/* One filled primary. The second action's weight depends on
                  whether the law requires a one-click refusal here.

                  OPT-IN (EEA/UK/CH): "Reject all" is outlined but sits in the
                  same row at the same size, so refusing costs exactly one
                  click. EDPB Guidelines 03/2022 on deceptive design target
                  asymmetric EFFORT — extra clicks, or burying refusal a layer
                  down — not a filled-vs-outlined pair, so the visual step down
                  is fine while the click parity is not negotiable. (Two filled
                  primaries used to sit here; that read as an unresolved choice
                  and gave the sheet two focal points.)

                  OPT-OUT (US): trackers may run until the visitor objects, so
                  there is no one-click-refusal requirement and the secondary
                  action drops to the quiet link it always was. It opens the
                  same panel, which under this regime starts with the optional
                  category ON — the "Do Not Sell or Share" flow. That default
                  is reachable ONLY where `resolveConsentRegime` says so: in
                  the EEA a pre-ticked category is not consent (Planet49), and
                  refusal behind a panel is the asymmetry CNIL fined Google and
                  Meta over in January 2022. */}
              <button
                onClick={handleAcceptAll}
                className="btn-primary text-xs px-4 py-1.5 rounded-lg"
              >
                {t('cookies.acceptAll')}
              </button>
              {regime === 'opt-in' ? (
                <button
                  onClick={handleRejectAll}
                  className="text-xs px-4 py-1.5 rounded-lg border border-border hover:bg-accent transition-colors"
                >
                  {t('cookies.rejectAll')}
                </button>
              ) : null}
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5"
              >
                {t('cookies.manage')}
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <div className="mt-3 space-y-2.5">
              {/* CPRA requires the sale/share opt-out to be presented as that
                  RIGHT, in those words — a toggle labelled "Third-party" does
                  not discharge it. Under the opt-out regime the panel is
                  therefore headed with the right being exercised, and the
                  toggle below is the mechanism. Not rendered under opt-in,
                  where the framing is consent rather than objection. */}
              {regime === 'opt-out' ? (
                <p className="text-xs font-semibold leading-snug">
                  {t('cookies.doNotSell')}
                </p>
              ) : null}
              {CATEGORIES.map(({ key, label, description, locked }) => (
                <button
                  key={key}
                  type="button"
                  className={`flex items-start gap-2.5 text-left group w-full ${locked ? 'cursor-default' : 'cursor-pointer'}`}
                  onClick={() => !locked && handleToggle(key)}
                  disabled={locked}
                >
                  <span className="pt-0.5 shrink-0">
                    <span
                      className={`relative block h-4 w-7 rounded-full transition-colors ${
                        prefs[key]
                          ? locked ? 'bg-primary/50' : 'bg-primary'
                          : 'bg-muted-foreground/25'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${
                          prefs[key] ? 'translate-x-3' : ''
                        }`}
                      />
                    </span>
                  </span>
                  <span className="min-w-0">
                    <span className="text-xs font-medium flex items-center gap-1.5">
                      {label}
                      {locked && (
                        <span className="text-[10px] text-muted-foreground font-normal">{t('cookies.required')}</span>
                      )}
                    </span>
                    <span className="text-[11px] text-muted-foreground leading-snug block mt-0.5">
                      {description}
                    </span>
                  </span>
                </button>
              ))}
              <div className="flex gap-2 pt-1">
                <button onClick={handleSave} className="btn-primary text-xs px-4 py-1.5 rounded-lg">
                  {t('cookies.savePreferences')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5"
                >
                  {t('cookies.acceptAll')}
                </button>
                <button
                  onClick={handleRejectAll}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5"
                >
                  {t('cookies.rejectAll')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}
